import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CloseIcon from '@mui/icons-material/Close'
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined'
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined'
import { FormFieldType, FormTemplate, AIOutputConfig, READINESS_CHECKLIST_CATEGORY } from '@/core/models/form.model'
import { Trans, t } from '@lingui/macro'
import FormTemplateFieldRow from './FormTemplateFieldRow'
import { PdfWorkflowDialog } from './PdfWorkflowDialog'
import FilePreview from '@/components/ui/preview/FilePreview'
import { DEFAULT_FORM_CATEGORIES, DEFAULT_FORM_TAGS } from '../constants'
import { getCategoryLabel, getTagLabel } from '../vocabularyLabels'
import { useLanguage } from '@/hooks/useLanguage'
import { useSnackbar } from 'notistack'
import VocabularyPicker from './VocabularyPicker'
import AiPdfConfigSection from './AiPdfConfigSection'
import { withChecklistCategory } from '../lib/formTemplateClassification'

interface Field {
  _id?: string
  label: string
  type: FormFieldType
  required: boolean
  options?: string[]
  category?: string
}

interface EditFormTemplateDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (
    data: { title: string; description: string; fields: Field[]; categories: string[]; tags: string[]; aiOutputConfig?: AIOutputConfig },
    shouldClose?: boolean
  ) => void | Promise<void>
  template: FormTemplate
  isSubmitting?: boolean
  onUploadPdf: (file: File, templateId: string) => Promise<any>
  onDeletePdf: (file: any, templateId: string) => Promise<void>
  onDetectFields: (templateId: string) => Promise<any[]>
  onBurnPdf: (templateId: string) => Promise<any>
}

const labelSx = { display: 'block', fontWeight: 600, fontSize: '0.875rem', color: 'text.primary', mb: 1 } as const
const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } } as const

const EditFormTemplateDialogComponent: React.FC<EditFormTemplateDialogProps> = ({
  open,
  onClose,
  onSubmit,
  template,
  isSubmitting = false,
  onUploadPdf,
  onDeletePdf,
  onDetectFields,
  onBurnPdf,
}) => {
  const [title, setTitle] = React.useState(template.title)
  const [description, setDescription] = React.useState(template.description || '')
  const [categories, setCategories] = React.useState<string[]>((template.categories ?? []).filter((category) => category !== READINESS_CHECKLIST_CATEGORY))
  const [isChecklist, setIsChecklist] = React.useState((template.categories ?? []).includes(READINESS_CHECKLIST_CATEGORY))
  const [tags, setTags] = React.useState<string[]>(template.tags ?? [])
  const lang = useLanguage()
  const { enqueueSnackbar } = useSnackbar()
  const [fields, setFields] = React.useState<Field[]>(
    template.fields.map((field) => ({
      ...field,
      type: field.type as FormFieldType, // Convert string to enum
    }))
  )
  const [aiOutputConfig, setAiOutputConfig] = React.useState<AIOutputConfig>(template.aiOutputConfig || { enabled: false })
  const [visibleFieldsCount, setVisibleFieldsCount] = React.useState(10)

  const [isWorkflowOpen, setIsWorkflowOpen] = React.useState(false)
  const [previewFile, setPreviewFile] = React.useState<any>(null)

  const handleShowMore = () => {
    setVisibleFieldsCount((prev) => Math.min(prev + 10, fields.length))
  }

  // Update state when template changes
  React.useEffect(() => {
    setTitle(template.title)
    setDescription(template.description || '')
    setFields(
      template.fields.map((field) => ({
        ...field,
        type: field.type as FormFieldType,
      }))
    )
    setCategories((template.categories ?? []).filter((category) => category !== READINESS_CHECKLIST_CATEGORY))
    setIsChecklist((template.categories ?? []).includes(READINESS_CHECKLIST_CATEGORY))
    setTags(template.tags ?? [])
    setAiOutputConfig(template.aiOutputConfig || { enabled: false })
  }, [template])

  const handleAddField = React.useCallback(() => {
    setFields((prev) => [
      ...prev,
      {
        label: '',
        type: FormFieldType.Text,
        required: false,
        options: [], // Always initialize options array
      },
    ])
  }, [])

  const handleRemoveField = React.useCallback((index: number) => {
    setFields((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleFieldChange = React.useCallback((index: number, field: Partial<Field>) => {
    setFields((prev) =>
      prev.map((f, i) => {
        if (i !== index) return f

        const updatedField = { ...f, ...field }

        // Initialize or clear options when changing type
        if (field.type) {
          if (field.type === FormFieldType.Multiple) {
            updatedField.options = updatedField.options || []
          } else {
            updatedField.options = undefined
          }
        }

        return updatedField
      })
    )
  }, [])

  const handleSubmit = async () => {
    try {
      await onSubmit({
        title,
        description,
        categories: withChecklistCategory(categories, isChecklist),
        tags,
        fields: fields.map((field) => ({ ...field, options: field.options?.map((option) => option.trim()).filter((option) => option.length > 0) })),
        aiOutputConfig,
      })
      enqueueSnackbar(t`Form template updated`, { variant: 'success' })
    } catch {
      enqueueSnackbar(t`Failed to save the form template. Please try again.`, { variant: 'error' })
    }
  }

  const handleSaveTemplate = async (updates: Partial<FormTemplate>) => {
    const currentData = {
      title,
      description,
      categories: withChecklistCategory(categories, isChecklist),
      tags,
      fields: fields.map((field) => ({ ...field, options: field.options?.map((option) => option.trim()).filter((option) => option.length > 0) })),
      aiOutputConfig,
      ...updates,
    }

    try {
      await onSubmit(currentData as any, false)
    } catch {}
  }

  const handleClose = () => {
    // Reset to initial values
    setTitle(template.title)
    setDescription(template.description || '')
    setFields(
      template.fields.map((field) => ({
        ...field,
        type: field.type as FormFieldType, // Convert string to enum
      }))
    )
    setCategories((template.categories ?? []).filter((category) => category !== READINESS_CHECKLIST_CATEGORY))
    setIsChecklist((template.categories ?? []).includes(READINESS_CHECKLIST_CATEGORY))
    setTags(template.tags ?? [])
    setAiOutputConfig(template.aiOutputConfig || { enabled: false })
    onClose()
  }

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        {/* Header */}
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, py: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <EditNoteOutlinedIcon sx={{ color: 'primary.main' }} />
            <Typography component="span" sx={{ fontWeight: 700, fontSize: '1.125rem', letterSpacing: '-0.01em' }}>
              {t`Edit form template`}
            </Typography>
          </Stack>
          <IconButton onClick={handleClose} size="small" sx={{ color: 'text.secondary' }} aria-label={t`Cancel`}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ bgcolor: 'background.default', p: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 3,
              p: { xs: 2.5, sm: 4 },
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            {/* Title */}
            <Box>
              <Typography component="label" sx={labelSx}>
                {t`Form title`}{' '}
                <Box component="span" sx={{ color: 'error.main' }} aria-hidden>
                  *
                </Box>
              </Typography>
              <TextField fullWidth required value={title} onChange={(e) => setTitle(e.target.value)} sx={inputSx} />
            </Box>

            {/* Description */}
            <Box>
              <Typography component="label" sx={labelSx}>
                {t`Form description`}{' '}
                <Box component="span" sx={{ color: 'error.main' }} aria-hidden>
                  *
                </Box>
              </Typography>
              <TextField fullWidth required value={description} onChange={(e) => setDescription(e.target.value)} multiline rows={3} sx={inputSx} />
            </Box>

            <VocabularyPicker
              label={t`Categories`}
              options={DEFAULT_FORM_CATEGORIES}
              value={categories}
              onChange={setCategories}
              getLabel={(option) => getCategoryLabel(option, lang)}
            />

            <VocabularyPicker label={t`Tags`} options={DEFAULT_FORM_TAGS} value={tags} onChange={setTags} getLabel={(option) => getTagLabel(option, lang)} />

            {/* Waiting-room preparation checklist */}
            <Box>
              <FormControlLabel
                control={<Switch checked={isChecklist} onChange={(e) => setIsChecklist(e.target.checked)} />}
                label={<Trans>Use as a waiting-room preparation checklist</Trans>}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                <Trans>Checklist templates can be selected in a waiting room's readiness settings, so operators can tick items before the doctor joins.</Trans>
              </Typography>
            </Box>

            {/* Fields */}
            <Accordion
              defaultExpanded
              TransitionProps={{ unmountOnExit: true }}
              disableGutters
              sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'background.default' }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ListAltOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {t`Fields`} ({fields.length}){' '}
                    <Box component="span" sx={{ color: 'error.main' }} aria-hidden>
                      *
                    </Box>
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {fields.slice(0, visibleFieldsCount).map((field, index) => (
                  <FormTemplateFieldRow key={index} field={field} index={index} onChange={handleFieldChange} onRemove={handleRemoveField} />
                ))}

                {visibleFieldsCount < fields.length && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Button onClick={handleShowMore} variant="text">
                      <Trans>Show more fields</Trans> ({fields.length - visibleFieldsCount} remaining)
                    </Button>
                  </Box>
                )}

                <Button
                  fullWidth
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={handleAddField}
                  sx={{
                    mt: 2,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    color: 'primary.main',
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.main', color: 'primary.contrastText' },
                  }}
                >
                  <Trans>Add field</Trans>
                </Button>
              </AccordionDetails>
            </Accordion>

            <AiPdfConfigSection
              aiOutputConfig={aiOutputConfig}
              onChange={setAiOutputConfig}
              fillablePdf={template.fillablePdf}
              onConfigureWorkflow={() => setIsWorkflowOpen(true)}
              onPreviewPdf={setPreviewFile}
            />
          </Box>
        </DialogContent>

        {/* Action bar */}
        <DialogActions sx={{ px: { xs: 2.5, sm: 4 }, py: 2, bgcolor: 'background.paper', gap: 1 }}>
          <Button onClick={handleClose} sx={{ fontWeight: 700, color: 'primary.main', textTransform: 'none', borderRadius: 2, px: 3 }}>
            {t`Cancel`}
          </Button>
          <LoadingButton
            loading={isSubmitting}
            onClick={handleSubmit}
            disabled={isSubmitting || !title || !description || fields.length === 0}
            sx={{
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 2,
              px: 4,
              py: 1,
              color: '#ffffff',
              background: 'linear-gradient(135deg, #621bcb 0%, #7b3fe4 100%)',
              boxShadow: '0 8px 20px rgba(98, 27, 203, 0.18)',
              '&:hover': { background: 'linear-gradient(135deg, #5a06c3 0%, #7334db 100%)', boxShadow: '0 10px 24px rgba(98, 27, 203, 0.24)' },
              '&.Mui-disabled': {
                background: (theme) => theme.palette.action.disabledBackground,
                color: (theme) => theme.palette.action.disabled,
                boxShadow: 'none',
              },
            }}
          >
            {isSubmitting ? t`Saving...` : t`Save`}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <PdfWorkflowDialog
        open={isWorkflowOpen}
        onClose={() => setIsWorkflowOpen(false)}
        template={template}
        onUploadPdf={onUploadPdf}
        onDeletePdf={onDeletePdf}
        onDetectFields={onDetectFields}
        onBurnPdf={onBurnPdf}
        onSaveTemplate={async (updates) => {
          handleSaveTemplate(updates)
        }}
      />

      {previewFile && (
        <Dialog open={!!previewFile} onClose={() => setPreviewFile(null)} maxWidth="lg" fullWidth>
          <DialogTitle>
            {previewFile.filename}
            <IconButton
              aria-label="close"
              onClick={() => setPreviewFile(null)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ height: '80vh', p: 0 }}>
            <FilePreview fileName={previewFile.filename} fileType="pdf" filePath={previewFile.url} />
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

export default EditFormTemplateDialogComponent
